import { Icon, Text, Center, Box } from '@chakra-ui/react';
import { ESIGN_NOTIFICATION_TYPES } from '@/constants/esignature';
import { EsignDocumentNotification } from './EsignDocumentNotification';
import EmptyNotification from '@/assets/icons/nav/EmptyNotification';

import { Intro } from './GetIntro';
import { Bounced } from './Bounced';
import { Replied } from './Replied';
import { IntroMade } from './IntroMade';
import { IntroDeclined } from './IntroDeclined';
import { AddedAsSender } from './AddedAsSender';
import { RoleChange } from './RoleChange';
import { GroupIntro } from './GroupIntro';
import { IntroSkipped } from './IntroSkipped';
import { CandidateRejected } from './CandidateRejected';
import { ConsentAccepted } from './ConsentAccepted';
import { EmailNotFound } from './EmailNotFound';
import { FlowPausedForCandidates } from './FlowPausedForCandidates';
import { FlowStoppedForCandidates } from './FlowStoppedForCandidates';
import { ProjectInvitation } from './ProjectInvitation';
import { ProjectReview } from './ProjectReview';
import { CandidateMeetingBooked } from './meeting/Meeting';
import { ReviewScoreCard } from './score-card/Scorecard';
import {
  OfferRequestNotification,
  JobRequestNotification,
  ApprovalReviewerRejectedNotification,
  ApprovalReviewerApprovedNotification,
  OfferAcceptedNotification,
} from './approvals-notifications';
import { ScheduleInterviewNotification } from './ScheduleInterviewNotification';
import { NoteMentionNotification } from './note-mentions';
import {
  ReviewCompletedNotification,
  ReviewRequestNotification,
} from './reviews/';
import {
  FillAssessmentScorecardNotification,
  ReviewAssessmentScorecardNotification,
  ViewAssessmentNotification,
} from './assessment';
import {
  SurveyRequestNotification,
  SurveyReminderNotification,
  SurveySubmittedNotification,
} from './survey';
import { CsvDownloadNotification } from './csv-download';
import { DataExportNotification } from './data-export';
import { CandidateReferredNotification } from './CandidateReferredNotification';
import { AddedToJobHiringTeam } from './AddedToJobHiringTeam';
import {
  CandidateMarkedNoShowNotification,
  CandidateUndoNoShowNotification,
  ConfirmRSVPNotification,
  InterviewBookedNotification,
  InterviewCancelledNotification,
  InterviewFillScorecardNotification,
  InterviewRescheduledNotification,
  MeetingStartsIn10MinsNotification,
  ReviewCandidateNotification,
  SentAMessageNotification,
  InterviewRsvpStatusRecruiterNotification,
} from './new-meeting-notifications';
import { SharedCalendarNotificationWrapper } from './SharedCalendarNotification/SharedCalendarNotificationWrapper';
import { BackgroundCheckNotification } from './backgroundCheckNotification/BackgroundCheckNotification';
import ApplicationExport from './ApplicationExport';
import {
  RequisitionApprovalRequestNotification,
  RequisitionApprovedNotification,
  RequisitionRejectedNotification,
  RequisitionFilledNotification,
  RequisitionAssignedNotification,
} from './requisition-notifications';
import {
  ReferralSubmittedNotification,
  ReferralUpdatedNotification,
} from './referral-notifications';
import { ApiTokenResetNotification } from './api-management-notifications';
import {
  PatAboutToExpireNotification,
  PatExpiredNotification,
} from './pat-notifications';
import { ReconnectMailboxNotification } from './reconnect-integration/ReconnectMailboxNotification';
import { ReconnectCalendarNotification } from './reconnect-integration/ReconnectCalendarNotification';
import { ReconnectZoomNotification } from './reconnect-integration/ReconnectZoomNotification';
import { ReconnectMicrosoftMeetingRoomsNotification } from './reconnect-integration/ReconnectMicrosoftMeetingRoomsNotification';
import { FraudDetectionCreditNotification } from './fraud-detection-notifications';
import { CoordinatorCreditNotification } from './coordinator-notifications';
import {
  CandidateConsentRespondedNotification,
  CandidateConsentRecruiterContactedNotification,
  CandidateConsentSummaryNotification,
} from './consent-notifications';
import {
  CandidateAvailabilitySubmittedNotification,
  CandidateAvailabilityUpdatedNotification,
  CandidateAvailabilityExpiredNotification,
  CandidateAvailabilityReminderNotification,
} from './candidate-availability';
import { decode } from 'js-base64';
import type {
  NotificationListProps,
  NotificationComponentType,
  NotificationItem,
} from './NotificationList.interface';

// Exported so the notification-center coverage test can read the integer
// notification type keys and assert each is classified — moving this to a
// dedicated file would relocate ~90 collaborator imports for no real gain.
// eslint-disable-next-line react-refresh/only-export-components
export const notificationComponents = () =>
  new Map<number, NotificationComponentType>([
    [0, Intro],
    [1, IntroMade],
    [2, IntroDeclined],
    [6, Replied],
    [7, Bounced],
    [8, AddedAsSender],
    [9, RoleChange],
    [9999, GroupIntro],
    [12, IntroSkipped],
    [13, CandidateRejected],
    [14, ConsentAccepted],
    [29, EmailNotFound],
    [30, FlowPausedForCandidates],
    [31, FlowStoppedForCandidates],
    [34, ProjectInvitation],
    [35, ProjectReview],
    [37, JobRequestNotification], // when any step starts in job
    [65, OfferRequestNotification], // when any step starts in offer
    [90, RequisitionApprovalRequestNotification], // requisition approval request
    [91, RequisitionFilledNotification], // requisition filled
    [92, RequisitionAssignedNotification], // requisition assigned
    [43, NoteMentionNotification], // Notes mention notification
    [52, ReviewRequestNotification], // Review request notification
    [75, ReviewCompletedNotification],
    [50, InterviewCancelledNotification],
    [49, InterviewBookedNotification],
    [48, MeetingStartsIn10MinsNotification],
    [47, InterviewRescheduledNotification],
    [63, InterviewFillScorecardNotification],
    [46, ReviewScoreCard],
    [58, CandidateMeetingBooked],
    [59, InterviewRescheduledNotification],
    [60, InterviewCancelledNotification],
    [44, ConfirmRSVPNotification],
    [67, InterviewRsvpStatusRecruiterNotification], // interviewer/candidate RSVP -> recruiter
    [69, FillAssessmentScorecardNotification],
    [70, ViewAssessmentNotification],
    [71, ReviewAssessmentScorecardNotification],
    [56, CandidateReferredNotification],
    [76, ScheduleInterviewNotification],
    [77, AddedToJobHiringTeam],
    [80, CandidateMarkedNoShowNotification], // when candidate is marked as no show in interviews
    [81, CandidateUndoNoShowNotification], // when candidate is undid the no show marking in interviews
    [84, ReviewCandidateNotification], // Before 1hr of interview, to review candidate
    [82, SentAMessageNotification], // Interviewer sent a message to recruiter
    [85, SharedCalendarNotificationWrapper],
    [86, BackgroundCheckNotification], // when checkr integration is connected
    [87, BackgroundCheckNotification], // when checkr integration is disconnected
    [88, ApplicationExport], // when application is exported
    // Survey notifications
    [93, SurveyRequestNotification], // when survey is requested/assigned
    [94, SurveyReminderNotification], // survey reminder notification
    [95, SurveySubmittedNotification], // when survey is submitted
    [96, CsvDownloadNotification], // CSV download notification
    // Referral notifications
    [97, ReferralSubmittedNotification], // when candidate is referred to recruiters
    [98, ReferralUpdatedNotification], // when candidate status changes (to referrer)
    [100, OfferAcceptedNotification], // when offer is accepted by candidate
    // API management notifications
    [101, ApiTokenResetNotification], // when API token is rotated
    [123, PatAboutToExpireNotification], // when a personal access token is nearing expiry
    [124, PatExpiredNotification], // when a personal access token has expired
    [102, DataExportNotification], // when data export is ready for download

    // Certn notifications
    [103, BackgroundCheckNotification], // when checkr integration is connected
    [104, BackgroundCheckNotification], // when certn integration is disconnected

    [105, FraudDetectionCreditNotification], // fraud detection credit cap reached
    [119, CoordinatorCreditNotification], // AI coordinator credit cap reached
    // Candidate consent notifications
    [117, CandidateConsentRecruiterContactedNotification], // candidate contacted recruiter via consent portal
    [118, CandidateConsentRespondedNotification], // candidate accepted/refused consent request
    [122, CandidateConsentSummaryNotification], // account-level consent summary digest
    // Candidate availability notifications
    [106, CandidateAvailabilitySubmittedNotification], // candidate submitted availability
    [107, CandidateAvailabilityUpdatedNotification], // candidate updated availability
    [108, CandidateAvailabilityExpiredNotification], // candidate availability expired
    [109, CandidateAvailabilityReminderNotification], // candidate availability reminder
    [110, ScheduleInterviewNotification], // candidate availability contact recruiter
    [112, ReconnectMailboxNotification],
    [113, ReconnectCalendarNotification],
    [114, ReconnectZoomNotification],
    [115, ReconnectMicrosoftMeetingRoomsNotification],

    // Docusign e-sign (RTC types 126-131). All six share one card: the payload
    // is identical and the title arrives fully rendered, so there is nothing
    // per-event to branch on. Every id must be listed — an unmapped type falls
    // through `if (!Component) return null` and renders nothing at all.
    [ESIGN_NOTIFICATION_TYPES.SIGNED_BY_CANDIDATE, EsignDocumentNotification],
    [ESIGN_NOTIFICATION_TYPES.SIGNED_BY_EMPLOYER, EsignDocumentNotification],
    [ESIGN_NOTIFICATION_TYPES.SIGNED_BY_BOTH, EsignDocumentNotification],
    [ESIGN_NOTIFICATION_TYPES.SIGNED_CANDIDATE_ONLY, EsignDocumentNotification],
    [ESIGN_NOTIFICATION_TYPES.REFUSED_BY_CANDIDATE, EsignDocumentNotification],
    [ESIGN_NOTIFICATION_TYPES.REFUSED_BY_EMPLOYER, EsignDocumentNotification],
  ]);

export const NotificationList = ({
  components,
  setMarkAll = () => {},
  onClose = () => {},
  notifications = [],
}: NotificationListProps) => {
  if (!notifications.length) {
    return (
      <Center flex="1" flexDir="column">
        <Icon as={EmptyNotification} w="150px" h="150px" />
        <Text fontSize="16px" fontWeight="semiBold">
          No notifications yet
        </Text>
        <Text fontSize="14px" fontWeight="medium">
          Nothing to read for now. Take a break!
        </Text>
      </Center>
    );
  }

  return (
    <Box
      mt="12px"
      flex="1"
      overflow="auto"
      data-test-id="in-app-notification-list"
    >
      {notifications?.map((notification: NotificationItem) => {
        let Component = notificationComponents().get(
          notification.notification_type
        );

        // Handle entity_type differentiation for notifications 38 and 40
        if (
          notification.notification_type === 38 ||
          notification.notification_type === 40
        ) {
          try {
            const { custom_attributes } = notification;
            if (custom_attributes) {
              const data = JSON.parse(decode(custom_attributes)) as {
                entity_type?: string;
              };
              const entityType = data?.entity_type;

              if (entityType === 'requisition') {
                Component =
                  notification.notification_type === 38
                    ? RequisitionRejectedNotification
                    : RequisitionApprovedNotification;
              } else {
                // Use existing components for job/offer approvals
                Component =
                  notification.notification_type === 38
                    ? ApprovalReviewerRejectedNotification
                    : ApprovalReviewerApprovedNotification;
              }
            }
          } catch (error) {
            console.error('Error decoding custom attributes:', error);
            // Fallback to existing components if decoding fails
            Component =
              notification.notification_type === 38
                ? ApprovalReviewerRejectedNotification
                : ApprovalReviewerApprovedNotification;
          }
        }

        if (!Component) return null;
        return (
          <Component
            setMarkAll={setMarkAll}
            notification={notification}
            key={notification?.id}
            onClose={onClose}
            components={components}
          />
        );
      })}
    </Box>
  );
};
